import { useSeoMeta } from '@unhead/react';
import { Wifi, ImageIcon } from 'lucide-react';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { RelayListManager } from '@/components/RelayListManager';
import { BlossomServerManager } from '@/components/BlossomServerManager';
import { LoginArea } from '@/components/auth/LoginArea';
import { AppHeader } from '@/components/AppHeader';
import { useCurrentUser } from '@/hooks/useCurrentUser';

export default function Settings() {
  const { user } = useCurrentUser();
  
  useSeoMeta({
    title: 'Settings - Nostr Point',
  });

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <AppHeader subtitle="Settings" hideSettings />

      <main className="container mx-auto px-4 py-8 max-w-2xl space-y-6">
        {/* Account Section */}
        <Card>
          <CardHeader>
            <CardTitle>Account</CardTitle>
            <CardDescription>
              Manage your Nostr login
            </CardDescription>
          </CardHeader>
          <CardContent>
            {user ? (
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">
                  Logged in as:
                </p>
                <p className="font-mono text-sm break-all bg-muted p-2 rounded">
                  {user.pubkey}
                </p>
              </div>
            ) : (
              <div className="text-center py-4">
                <p className="text-muted-foreground mb-4">
                  Log in to sync your relay list and publish presentations
                </p>
                <LoginArea className="justify-center" />
              </div>
            )}
          </CardContent>
        </Card>

        {/* Relay Settings */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Wifi className="w-5 h-5 text-primary" />
              <CardTitle>Relays</CardTitle>
            </div>
            <CardDescription>
              Configure which Nostr relays Nostr Point uses for reading and publishing
              presentations. This is a local override prefilled from your published relay
              list (NIP-65); changes stay on this device.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <RelayListManager />
          </CardContent>
        </Card>

        {/* Media / Blossom Settings */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <ImageIcon className="w-5 h-5 text-primary" />
              <CardTitle>Media Servers</CardTitle>
            </div>
            <CardDescription>
              Images you upload are stored on Blossom servers. Configure where your
              media is hosted. The first server in the list is used first. This is a
              local override prefilled from your published server list; changes stay
              on this device.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <BlossomServerManager />
          </CardContent>
        </Card>

        {/* About */}
        <Card>
          <CardHeader>
            <CardTitle>About</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm text-muted-foreground">
            <p>
              <strong>Nostr Point</strong> is a decentralized presentation app built on the Nostr protocol.
            </p>
            <p>
              Create slide decks, share them with anyone, and present with confidence — even offline.
            </p>
            <p className="pt-2">
              Uses <strong>Kind 31361</strong> for addressable slide presentations.
            </p>
            <p className="pt-4">
              Vibed with{' '}
              <a 
                href="https://shakespeare.diy" 
                target="_blank" 
                rel="noopener noreferrer"
                className="underline hover:text-foreground"
              >
                Shakespeare
              </a>
            </p>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
